"use client";

import { cn, getTextColorForBackground, rgbToHex, shadow } from "@/lib/utils";
import { IAccountAssets, IBlog, IBlogPost, ITheme, IUserData } from "@/types";
import React from "react";
import { Badge } from "@/components/ui/badge";
import moment from "moment";
import { Label } from "@/components/ui/label";
import Link from "next/link";
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import EditorComponent from "../BlockNote";
import { Button } from "@/components/ui/button";
import { IconCircle, IconCircleFilled, IconX } from "@tabler/icons-react";
import { UserURL } from "@/functions";
import {
    BORDER_RADIUS_OPTIONS,
    SHADOW_OPTIONS,
} from "@/components/render/blocks/utils/block-style-classes";

interface Props {
    assets: IAccountAssets[];
    blog: IBlog;
    post: IBlogPost;
    account: IUserData;
    adminPage?: boolean;
    className?: string;
    brand_id?: string | null;
    theme?: IBlog["theme"];
    accountTheme: ITheme;
    url?: string;
    changelog?: boolean;
    categoryView?: boolean;
    isFirst?: boolean;
    second?: boolean;
    third?: boolean;
    fourth?: boolean;
    fifth?: boolean;
    sixth?: boolean;
    dorasposts?: boolean;
    v2Theme?: IUserData["chosen_theme"]["blog"];
    list?: boolean;
}
export default function PostList({
    assets,
    blog,
    post,
    account,
    adminPage,
    className,
    brand_id,
    theme,
    accountTheme,
    url,
    changelog,
    categoryView = false,
    isFirst,
    second,
    third,
    fourth,
    fifth,
    sixth,
    dorasposts = false,
    v2Theme,
    list,
}: Props) {
    const extractTextContent = (content: any[]) => {
        return content
            .map((item) => {
                if (
                    item.type == "paragraph" ||
                    (item.type == "heading" &&
                        item.content &&
                        item.content.length > 0)
                ) {
                    return item.content
                        .map((contentItem: any) => contentItem.text || "")
                        .join(" ");
                }
                return "";
            })
            .join(" ")
            .trim();
    };
    const contentPreview = extractTextContent(post.content);
    if (changelog) {
        return (
            <Sheet>
                <SheetTrigger asChild>
                    <div className="flex cursor-pointer flex-col gap-1 border-b bg-card p-1 hover:bg-muted/50">
                        <div className="flex items-center justify-between gap-2">
                            <Label variant={"toggle"} className="line-clamp-1">
                                v{post.title}
                            </Label>
                            <Label
                                variant={"subtoggle"}
                                className="line-clamp-1"
                            >
                                {moment(post.created_at).format("Do MMM YYYY")}
                            </Label>
                        </div>
                        <p className="line-clamp-3 text-sm text-muted-foreground">
                            {contentPreview}.....
                        </p>
                    </div>
                </SheetTrigger>
                <SheetContent className="overflow-y-auto overflow-x-hidden p-0 sm:max-w-[90vw] md:max-w-[50vw]">
                    <SheetHeader className="sticky top-0 z-50 border-b bg-card p-3">
                        <div className="flex items-center justify-between">
                            <SheetTitle>v{post.title}</SheetTitle>
                            <SheetClose asChild>
                                <Button variant="ghost" size="icon">
                                    <IconX />
                                </Button>
                            </SheetClose>
                        </div>
                        <div className="flex items-center justify-between">
                            <SheetDescription>
                                Released{" "}
                                {moment(post.created_at).format(
                                    "MMMM Do, YYYY"
                                )}
                            </SheetDescription>
                            <a href="/changelog" target="_blank">
                                <Button variant="outline" size="sm">
                                    See full changelog
                                </Button>
                            </a>
                        </div>
                    </SheetHeader>
                    <div className="flex flex-col gap-2 p-3 pb-12">
                        <EditorComponent
                            value={post.content}
                            id={"changelogsheet"}
                            key={"editor"}
                            readOnly
                            account={account}
                        />
                    </div>
                </SheetContent>
            </Sheet>
        );
    }
    if (dorasposts) {
        return (
            <Link
                prefetch={true}
                href={
                    url
                        ? url.replace("{url}", post.slug)
                        : `https://${UserURL(account)}/post/${post.slug}`
                }
                className={cn(
                    `flex h-full w-full flex-col gap-1 ${shadow(accountTheme.button_shadow)} rounded-xl transition-all duration-150 `,
                    className,
                    v2Theme && SHADOW_OPTIONS[v2Theme.design.shadow]?.class
                )}
                style={{
                    color: v2Theme
                        ? v2Theme.colors.primary_text
                        : accountTheme.text_col_button,
                    background: v2Theme
                        ? v2Theme.colors.primary
                        : accountTheme.inset
                            ? "transparent"
                            : accountTheme.button,
                    border: v2Theme
                        ? `2px solid ${v2Theme.colors.tertiary}`
                        : accountTheme.inset
                            ? `2px solid ${accountTheme.button}`
                            : "none",
                }}
            >
                <img
                    src={post.img}
                    className={`h-48 w-full rounded-xl object-cover object-center`}
                    alt=""
                />
                <div className="px-2 py-1">
                    <p
                        className={cn("line-clamp-1 text-sm font-bold")}
                        style={{
                            fontFamily: v2Theme?.design.font.heading || "",
                        }}
                    >
                        {post.title}
                    </p>
                    <p
                        className={cn("line-clamp-4 text-xs")}
                        style={{ fontFamily: v2Theme?.design.font.body || "" }}
                    >
                        {contentPreview}
                    </p>
                </div>
            </Link>
        );
    }

    // Category grid view (non-list variant)
    if (categoryView && !list) {
        return (
            <Link
                prefetch={true}
                href={
                    url
                        ? url.replace("{url}", post.slug)
                        : `https://${UserURL(account)}/post/${post.slug}`
                }
                className={cn(
                    "flex w-full flex-col gap-1 overflow-hidden",
                    shadow(accountTheme.button_shadow),
                    "rounded-xl",
                    "h-full  scale-100 transition-all duration-150 md:col-span-full",
                    isFirst && "md:col-span-4 md:row-span-2",
                    second && "md:col-span-2",
                    third && "md:col-span-2",
                    v2Theme && SHADOW_OPTIONS[v2Theme.design.shadow]?.class,
                    v2Theme &&
                    //@ts-expect-error
                    BORDER_RADIUS_OPTIONS[v2Theme.design?.corner_radius]
                        ?.class
                )}
                style={{
                    color: v2Theme
                        ? v2Theme.colors.primary_text
                        : accountTheme.text_col_button,
                    background: v2Theme
                        ? v2Theme.colors.primary
                        : accountTheme.inset
                            ? "transparent"
                            : accountTheme.button,
                    border: v2Theme
                        ? `2px solid ${v2Theme.colors.tertiary}`
                        : accountTheme.inset
                            ? `2px solid ${accountTheme.button}`
                            : "none",
                }}
            >
                <div
                    className={cn(
                        `relative flex h-full min-h-full flex-col items-start gap-2`,
                        (second || third) &&
                        "flex-row items-center md:flex-col md:items-start",
                        (fourth || fifth || sixth) && "flex-row items-center"
                    )}
                >
                    {!changelog && (
                        <div className="relative">
                            <img
                                src={post.img}
                                className={cn(
                                    " aspect-video h-48 w-full object-cover object-center",
                                    "rounded-xl",
                                    isFirst && "h-fit",
                                    (second || third) &&
                                    "aspect-video h-32 w-full md:h-48 md:w-full",
                                    (fourth || fifth || sixth) &&
                                    "aspect-video h-32 w-full md:h-36"
                                )}
                                alt=""
                            />
                        </div>
                    )}
                    <div className="relative flex h-full w-fit flex-col justify-between gap-1 px-3 pb-2">
                        <div className="flex h-full flex-col gap-1">
                            <p
                                className={cn(
                                    "line-clamp-1 text-xl font-bold",
                                    isFirst && "text-3xl",
                                    (second || third) && "pt-2 md:p-0",
                                    (fourth || fifth || sixth) && "pt-2"
                                )}
                                style={{
                                    fontFamily:
                                        v2Theme?.design.font.heading || "",
                                }}
                            >
                                {post.title}
                            </p>
                            <p
                                className={cn(
                                    "line-clamp-1 text-lg md:hidden",
                                    isFirst && "md:line-clamp-3",
                                    second &&
                                    "max-w-prose text-sm md:line-clamp-2 md:hidden",
                                    third &&
                                    "max-w-prose text-sm md:line-clamp-2 md:hidden",
                                    (fourth || fifth || sixth) &&
                                    "max-w-prose text-sm md:line-clamp-2"
                                )}
                                style={{
                                    fontFamily: v2Theme?.design.font.body || "",
                                }}
                            >
                                {contentPreview}
                            </p>
                            <div
                                className={cn(
                                    "flex items-center gap-1",
                                    (fourth || fifth || sixth) && "mt-auto"
                                )}
                                style={{
                                    color: v2Theme
                                        ? v2Theme.colors.primary_text
                                        : accountTheme.text_col_button,
                                }}
                            >
                                <p className={`p-1 text-xs font-bold`}>
                                    {post?.authorAccount?.displayname}
                                </p>
                                <IconCircleFilled className="p-0.5" size={10} />
                                <p className={`p-1 text-xs font-bold`}>
                                    {moment(post.created_at).format(
                                        "Do MMMM, YYYY"
                                    )}
                                </p>
                            </div>
                        </div>

                        <p
                            className={`block text-sm md:hidden`}
                            style={{
                                color: v2Theme
                                    ? v2Theme.colors.primary_text
                                    : accountTheme.text_col_button,
                            }}
                        >
                            Posted on{" "}
                            {moment(post.created_at).format("Do MMM YYYY")}
                        </p>
                    </div>
                </div>
            </Link>
        );
    }
    if (categoryView && list == true) {
        return (
            <CategoryViewListPost
                post={post}
                url={url}
                account={account}
                accountTheme={accountTheme}
                v2Theme={v2Theme}
                changelog={changelog}
                isFirst={isFirst}
            />
        );
    }
    return (
        <Link
            prefetch={true}
            href={
                url
                    ? url.replace("{url}", post.slug)
                    : `https://${UserURL(account)}/post/${post.slug}`
            }
            className={cn(
                `flex h-36 max-h-36 w-full flex-col gap-1 overflow-hidden ${shadow(accountTheme.button_shadow)} rounded-xl
            transition-all duration-150 hover:scale-105`,
                className,
                v2Theme && SHADOW_OPTIONS[v2Theme.design.shadow]?.class,
                v2Theme &&
                //@ts-expect-error
                BORDER_RADIUS_OPTIONS[v2Theme.design?.corner_radius]?.class
            )}
            style={{
                color: v2Theme
                    ? v2Theme.colors.primary_text
                    : accountTheme.text_col_button,
                background: v2Theme
                    ? v2Theme.colors.primary
                    : accountTheme.inset
                        ? "transparent"
                        : accountTheme.button,
                border: v2Theme
                    ? `2px solid ${v2Theme.colors.tertiary}`
                    : accountTheme.inset
                        ? `2px solid ${accountTheme.button}`
                        : "none",
            }}
        >
            <div
                className={`relative flex h-full min-h-full flex-col items-start gap-2 md:flex-row`}
            >
                {!changelog && (
                    <img
                        src={post.img}
                        className={`rounded-xl} aspect-square h-20 w-full object-cover 
                        object-center md:h-full md:w-36
                        
                        `}
                        alt=""
                    />
                )}
                <div className="relative flex h-full w-full flex-col justify-between gap-1 pl-2 pr-6 md:pl-0 md:pt-3">
                    <div className="flex h-full flex-col gap-1">
                        <p className="line-clamp-1 text-xl font-bold">
                            {post.title}
                        </p>
                        {/* )} */}
                        <p
                            className="line-clamp-3 text-sm"
                            style={{
                                fontFamily: v2Theme?.design?.font?.body || "",
                            }}
                        >
                            {contentPreview}
                        </p>
                    </div>
                    <div className="mt-0 flex w-full items-start justify-between pt-1">
                        <div className="relative z-50 flex items-center gap-2 pb-2">
                            <Badge
                                className={`rounded-xl`}
                                style={{
                                    background: v2Theme
                                        ? v2Theme.colors.secondary
                                        : accountTheme.accent_col ||
                                        accountTheme.button,
                                    color: v2Theme
                                        ? v2Theme.colors.secondary_text
                                        : (accountTheme.accent_col &&
                                            getTextColorForBackground(
                                                rgbToHex(
                                                    accountTheme.accent_col
                                                )
                                            )) ||
                                        accountTheme.text_col_button ||
                                        accountTheme.button,
                                }}
                            >
                                {post.category_name}
                            </Badge>
                        </div>
                        <p
                            className={`hidden text-sm md:block`}
                            style={{
                                color: v2Theme
                                    ? v2Theme.colors.primary_text
                                    : accountTheme.text_col_button,
                            }}
                        >
                            Posted on{" "}
                            {moment(post.created_at).format("Do MMM YYYY")}
                        </p>
                    </div>
                    <p
                        className={`block text-sm md:hidden`}
                        style={{
                            color: v2Theme
                                ? v2Theme.colors.primary_text
                                : accountTheme.text_col_button,
                        }}
                    >
                        Posted on{" "}
                        {moment(post.created_at).format("Do MMM YYYY")}
                    </p>
                </div>
            </div>
        </Link>
    );
}

// Extracted list-style category view post component for clarity & reuse.
interface CategoryViewListPostProps {
    post: IBlogPost;
    url?: string;
    account: IUserData;
    accountTheme: ITheme;
    v2Theme?: IUserData["chosen_theme"]["blog"];
    changelog?: boolean;
    isFirst?: boolean;
}

function CategoryViewListPost({
    post,
    url,
    account,
    accountTheme,
    v2Theme,
    changelog,
    isFirst,
}: CategoryViewListPostProps) {
    const featured = !!isFirst;

    // Recompute lightweight preview (defensive: original logic lives above in parent; avoid tight coupling)
    const extractTextContent = (content: any[]) =>
        content
            ?.map((item: any) => {
                if (
                    item?.type === "paragraph" ||
                    (item?.type === "heading" && item?.content?.length > 0)
                ) {
                    return item.content.map((c: any) => c.text || "").join(" ");
                }
                return "";
            })
            .join(" ")
            .trim();
    const contentPreview = extractTextContent(post.content);

    return (
        <Link
            prefetch={true}
            href={
                url
                    ? url.replace("{url}", post.slug)
                    : `https://${UserURL(account)}/post/${post.slug}`
            }
            className={cn(
                "group flex w-full flex-col overflow-hidden",
                shadow(accountTheme.button_shadow),
                "rounded-xl transition-all duration-150",
                featured ? "md:col-span-full md:row-span-1" : "md:col-span-3",
                v2Theme && SHADOW_OPTIONS[v2Theme.design.shadow]?.class,
                v2Theme &&
                //@ts-expect-error
                BORDER_RADIUS_OPTIONS[v2Theme.design?.corner_radius]?.class
            )}
            style={{
                color: v2Theme
                    ? v2Theme.colors.primary_text
                    : accountTheme.text_col_button,
                background: v2Theme
                    ? v2Theme.colors.primary
                    : accountTheme.inset
                        ? "transparent"
                        : accountTheme.button,
                border: v2Theme
                    ? `2px solid ${v2Theme.colors.tertiary}`
                    : accountTheme.inset
                        ? `2px solid ${accountTheme.button}`
                        : "none",
            }}
            id="blog-post-item"
        >
            <div
                className={cn(
                    "relative flex h-full w-full flex-col",
                    featured ? "items-start gap-4 md:flex-row" : "gap-4"
                )}
            >
                {!changelog && (
                    <div
                        id="blog-post-image-root"
                        className={cn(
                            "relative w-full",
                            featured ? "md:w-1/3" : ""
                        )}
                    >
                        <img
                            src={post.img}
                            alt=""
                            id="blog-post-image"
                            className={cn(
                                "w-full rounded-xl object-cover object-center",
                                featured
                                    ? "aspect-video h-64 md:h-80"
                                    : "aspect-video h-64 md:h-80"
                            )}
                        />
                    </div>
                )}
                <div
                    className={cn(
                        "flex h-full flex-1 flex-col justify-between px-4 pb-3 pt-2",
                        featured ? "px-6 pb-4" : "px-6 pb-4"
                    )}
                >
                    <div className="flex flex-col gap-2">
                        <p
                            id="blog-post-title"
                            className={cn(
                                "font-bold",
                                featured
                                    ? "line-clamp-2 text-3xl leading-tight"
                                    : "line-clamp-2 text-3xl leading-tight"
                            )}
                            style={{
                                fontFamily: v2Theme?.design.font.heading || "",
                            }}
                        >
                            {post.title}
                        </p>
                        <p
                            className={cn(
                                featured
                                    ? "line-clamp-3 text-base md:text-lg"
                                    : "line-clamp-3 text-base md:text-lg"
                            )}
                            style={{
                                fontFamily: v2Theme?.design.font.body || "",
                            }}
                            id="blog-post-content"
                        >
                            {contentPreview}
                        </p>
                    </div>
                    <div
                        className="mt-3 flex flex-wrap items-center gap-1 text-xs font-bold"
                        id="blog-post-meta"
                        style={{
                            color: v2Theme
                                ? v2Theme.colors.primary_text
                                : accountTheme.text_col_button,
                        }}
                    >
                        <p>{post?.authorAccount?.displayname}</p>
                        <IconCircleFilled className="p-0.5" size={10} />
                        <p>{moment(post.created_at).format("Do MMMM, YYYY")}</p>
                    </div>
                    <p
                        className={cn(
                            "mt-2 block text-sm md:hidden",
                            featured && "text-sm"
                        )}
                        style={{
                            color: v2Theme
                                ? v2Theme.colors.primary_text
                                : accountTheme.text_col_button,
                        }}
                        id="blog-post-date"
                    >
                        Posted on{" "}
                        {moment(post.created_at).format("Do MMM YYYY")}
                    </p>
                </div>
            </div>
        </Link>
    );
}
